/**
 * Static copy + the credit ladder for the "General plan" pricing card.
 *
 * 1:1 rebuild of the Figma design (Deck v0.8, node 490:17942): a single card with a free 30-credit
 * baseline, an Annual switch, a credit-volume selector and a gradient Upgrade button on the left, and
 * a "What you get" feature list (custom SVG icons) on the right.
 *
 * Why static: the page is normally data-driven from Stripe (`usePricing` → `getPricingPlans`), but the
 * launch copy + the displayed ladder ($30/200 → $240/2,000) don't live cleanly in price metadata. The
 * Upgrade button still resolves to real backend price IDs by credit count (see PricingTable), so
 * checkout/redirect keep working; once the Live catalog matches this ladder it's an exact 1:1.
 *
 * Source: /PRICING_PAGE_COPY.md + Figma node 490:17942.
 */

export interface PlanFeature {
  /** DeckIcon name — the SVG file stem in design-system/icons/svgs. */
  icon: string;
  text: string;
}

export interface LadderStep {
  credits: number;
  /** Monthly price, in cents. */
  monthlyAmount: number;
  /** Full yearly price, in cents (= monthly × 12 × 0.8). */
  yearlyAmount: number;
}

/**
 * Timing for the slider's headline numbers (credits + price), now rolled by Scritto.
 *
 * 260, down from NumberFlow's 500. `duration` in Scritto is ONE GLYPH's roll and the last glyph
 * starts late, so the whole change lands somewhere past it — and the docs are explicit that a value
 * changing faster than its own roll stacks ghosts of the leaving glyphs. These numbers are driven by
 * a slider being dragged, which is the fastest a value in this product ever changes, so the roll has
 * to finish inside a drag step rather than inside a click.
 *
 * `easing` is the pre-redesign slider's own curve, so the digits and the bar they belong to travel
 * on the same one.
 */
export const NUMBERFLOW_TIMING = { duration: 260, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' } as const;

export const PLAN_TITLE = 'General plan';
export const FREE_CREDITS = 100;
export const FREE_LABEL = 'Free';
export const PLAN_SUBTITLE = 'Everything Deck does, free to try';
export const BUY_MORE_LABEL = 'Customize plan';
export const ANNUAL_LABEL = 'Annual (20% OFF)';
export const UPGRADE_LABEL = 'Upgrade';
export const WHAT_YOU_GET_LABEL = 'What you get:';

export const FEATURES: PlanFeature[] = [
  { icon: 'agent', text: 'A real AI agent, which plans and executes' },
  { icon: 'edit-ai', text: 'Edit any slide like in Figma, every pixel' },
  { icon: 'image-reference', text: 'Give it any reference and it builds to match' },
  { icon: 'models', text: 'Use any AI model, the best ones included' },
  { icon: 'document-ai', text: 'Drop in your own docs, notes and research' },
  { icon: 'image-upload', text: 'Upload and store own images' },
  { icon: 'link-extract', text: "We pull the site's look, colors and images" },
  { icon: 'cook-book-2', text: 'Create cookbook to automate agents' },
  { icon: 'integrations-2', text: 'Connect Figma, Linear, GitHub and Notion' },
];

/**
 * Pro credit ladder (PROPOSED 2026-06-18 — awaiting Guri's final numbers). More steps + a gentle
 * volume discount, honoring his anchors (200 = $30, 500 = $50). Yearly = monthly × 12 × 0.8 (20% off).
 * $/credit decreases with volume: 0.18 → 0.08. See PRICING_PAGE_COPY.md for margins.
 */
export const PRO_LADDER: LadderStep[] = [
  { credits: 100, monthlyAmount: 1800, yearlyAmount: 17280 },
  { credits: 200, monthlyAmount: 3000, yearlyAmount: 28800 },
  { credits: 300, monthlyAmount: 4000, yearlyAmount: 38400 },
  { credits: 500, monthlyAmount: 5000, yearlyAmount: 48000 },
  { credits: 1000, monthlyAmount: 10000, yearlyAmount: 96000 },
  { credits: 2000, monthlyAmount: 20000, yearlyAmount: 192000 },
];
